import { measureLatency } from '../../gctl-measure';
import { VideoFields, Video } from '../../models/components/schemas/video/Video';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';
import {
  AlbumVideoFields,
  AlbumVideo,
} from '../../models/components/schemas/album-video/AlbumVideo';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetAlbumVideosFields = readonly VideoFields[];

/** Options for GET /albums/{album_id}/videos */
export type GetAlbumVideosOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
  };

  /** Possible query parameters for /albums/{album_id}/videos */
  query?: {
    /**
     * The hashed password of the showcase from the desktop cookie.
     */
    hashedPass?: string;

    /**
     * The page containing the video URI.
     */
    containingUri?: string;

    /**
     * The sort direction of the results.
     *
     * Option descriptions:
     *  * `asc` - Sort the results in ascending order.
     *  * `desc` - Sort the results in descending order.
     *
     */
    direction?: `asc` | `desc`;

    /**
     * The attribute by which to filter the results.
     *
     * Option descriptions:
     *  * `embeddable` - Filter the results by the **embeddable** attribute.
     *  * `playable` - Filter the results by the **playable** attribute.
     *
     */
    filter?: `embeddable` | `playable`;

    /**
     * Whether to filter the results by embeddable videos (`true`) or non-embeddable videos (`false`). This parameter is required only when **filter** is `embeddable`.
     */
    filterEmbeddable?: boolean;

    /**
     * Whether the response can return a video collection to indicate videos within a collection like a showcase or channel.
     */
    forceCollectionUri?: boolean;

    /**
     * Whether the featured content of the showcase should be part of the response.
     */
    getFeatured?: boolean;

    /**
     * Whether to filter showcase videos by permitted domains.
     */
    isEmbed?: boolean;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The password of the showcase.
     */
    password?: string;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;

    /**
     * The search query to use to filter the results.
     */
    query?: string;

    /**
     * The domain where the request originated.
     */
    referrer?: string;

    /**
     * The way to sort the results.
     *
     * Option descriptions:
     *  * `alphabetical` - Sort the results alphabetically.
     *  * `comments` - Sort the results by number of comments.
     *  * `date` - Sort the results by date.
     *  * `default` - Sort the results by the default method.
     *  * `duration` - Sort the results by duration.
     *  * `likes` - Sort the results by number of likes.
     *  * `manual` - Sort the results by their manual order.
     *  * `modified_time` - Sort the results by last modification.
     *  * `plays` - Sort the results by number of plays.
     *
     */
    sort?:
      | `alphabetical`
      | `comments`
      | `date`
      | `default`
      | `duration`
      | `likes`
      | `manual`
      | `modified_time`
      | `plays`;

    /**
     * Whether to include private videos in the search. Please note that a separate search service provides this functionality. The service performs a partial text search on the video's name.
     */
    weakSearch?: boolean;
  };
};

/** Result type for GET /albums/{album_id}/videos */
export type GetAlbumVideosResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /albums/{album_id}/videos */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<Video, Paths>[];
};

/**
 * Get all the videos in a showcase
 * This method returns every video in the specified showcase. The authenticated user must be the owner of the showcase.
 */
export async function getAlbumVideos<Paths extends GetAlbumVideosFields>({
  baseUrl,
  select,
  where: { albumId },
  query,
  ...options
}: GetAlbumVideosOptions<Paths>): Promise<GetAlbumVideosResult<Paths>> {
  return measureLatency('getAlbumVideos', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/albums/${albumId}/videos?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetAlbumVideosResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PutAlbumVideosFields = readonly string[];

/** Options for PUT /albums/{album_id}/videos */
export type PutAlbumVideosOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
  };

  /** Data serialized to body for PUT /albums/{album_id}/videos */
  variables: {
    /**
     * A comma-separated list of video URIs corresponding to the videos to add.
     * @example /videos/258684937,/videos/273576296
     */

    videos: string;
  };
};

/** Result type for PUT /albums/{album_id}/videos */
export type PutAlbumVideosResult<Paths extends readonly string[]> = unknown;

/**
 * Replace all the videos in a showcase
 * This method replaces all the videos in the specified showcase with a new set of one or more videos. The authenticated user must be the owner of the showcase.
 */
export async function putAlbumVideos<Paths extends PutAlbumVideosFields>({
  baseUrl,
  variables,
  where: { albumId },
  ...options
}: PutAlbumVideosOptions<Paths>): Promise<PutAlbumVideosResult<Paths>> {
  return measureLatency('putAlbumVideos', 'PUT', async () => {
    let res = await fetch(`${baseUrl}/albums/${albumId}/videos`, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(deepSnakeCase(variables)),
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PutAlbumVideosResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchAlbumVideosFields = readonly AlbumVideoFields[];

/** Options for PATCH /albums/{album_id}/videos */
export type PatchAlbumVideosOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.albumvideo+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
  };

  /** Data serialized to body for PATCH /albums/{album_id}/videos */
  variables: {
    /**
     * The array of videos to remove from the showcase. Specify these with a batch request; see our [Using Common Formats and Parameters](https://developer.vimeo.com/api/common-formats#working-with-batch-requests) guide for more information.
     */

    remove?: {
      video: {
        /**
         * The URI of a video to remove from the showcase. This parameter goes in the **remove** array.
         * @example /videos/258684937
         */

        uri: string;
      };
    }[];

    /**
     * The array of videos to add or reposition in the showcase. Specify these with a batch request; see our [Using Common Formats and Parameters](https://developer.vimeo.com/api/common-formats#working-with-batch-requests) guide for more information.
     */

    set?: {
      video: {
        /**
         * The URI of a video to add or reposition in the showcase. This parameter goes in the **set** array.
         * @example 5
         */

        position?: number;

        /**
         * The sortable position of the video in the showcase. This parameter goes in the **set** array.
         * @example /videos/258684937
         */

        uri: string;
      };
    }[];
  };
};

/** Result type for PATCH /albums/{album_id}/videos */
export type PatchAlbumVideosResult<Paths extends readonly string[]> = Select<AlbumVideo, Paths>;

/**
 * Add, remove, or change the order of multiple videos in a showcase
 * This method can do any of the following: add multiple videos to the specified showcase, remove multiple videos from the showcase, or change the order of the videos in the showcase, depending on the body parameter. Use the **remove** parameter to remove videos and the **set** parameter to add new videos or reorder existing videos.
 *
 * Normally, the API appends videos to the showcase in ascending order, starting from position value `1` (for a newly created showcase).
 *
 * You can perform all three operations with the same request. The API processes the **remove** operation first and ignores any **set** operation that assigns a video to a position that's already occupied.
 *
 * The authenticated user must be the owner of the showcase.
 */
export async function patchAlbumVideos<Paths extends PatchAlbumVideosFields>({
  baseUrl,
  select,
  variables,
  where: { albumId },
  ...options
}: PatchAlbumVideosOptions<Paths>): Promise<PatchAlbumVideosResult<Paths>> {
  return measureLatency('patchAlbumVideos', 'PATCH', async () => {
    let res = await fetch(
      `${baseUrl}/albums/${albumId}/videos?fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchAlbumVideosResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
